import { useState, useEffect, useRef } from 'react';
import { getCurrentMention, filterUsersForMention } from '../utils/mentions';

interface MentionAutocompleteProps {
  text: string;
  cursorPosition: number;
  users: any[];
  onSelectMention: (username: string, startIndex: number, endIndex: number) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
}

export default function MentionAutocomplete({
  text,
  cursorPosition,
  users,
  onSelectMention,
  textareaRef,
}: MentionAutocompleteProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);
  const [currentMention, setCurrentMention] = useState<{ mention: string; startIndex: number } | null>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mention = getCurrentMention(text, cursorPosition);
    setCurrentMention(mention);

    if (mention) {
      const filtered = filterUsersForMention(users, mention.mention);
      setFilteredUsers(filtered);
      setIsVisible(filtered.length > 0);
      setSelectedIndex(0);
    } else {
      setIsVisible(false);
      setFilteredUsers([]);
    }
  }, [text, cursorPosition, users]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (!isVisible || filteredUsers.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, filteredUsers.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
        case 'Tab':
          e.preventDefault();
          handleSelectUser(filteredUsers[selectedIndex]);
          break;
        case 'Escape':
          e.preventDefault();
          setIsVisible(false);
          break;
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, filteredUsers, selectedIndex]);

  function handleSelectUser(user: any) {
    if (!currentMention) return;

    const endIndex = currentMention.startIndex + 1 + currentMention.mention.length;
    onSelectMention(user.username, currentMention.startIndex, endIndex);
    setIsVisible(false);
  }

  function getAutocompletePosition() {
    if (!textareaRef.current || !currentMention) return { top: 0, left: 0 };

    const textarea = textareaRef.current;
    const textBeforeCursor = text.substring(0, currentMention.startIndex);

    // Create a temporary element to measure text dimensions
    const tempElement = document.createElement('div');
    tempElement.style.cssText = getComputedStyle(textarea).cssText;
    tempElement.style.position = 'absolute';
    tempElement.style.visibility = 'hidden';
    tempElement.style.height = 'auto';
    tempElement.style.width = textarea.clientWidth + 'px';
    tempElement.style.whiteSpace = 'pre-wrap';
    tempElement.style.wordWrap = 'break-word';
    tempElement.textContent = textBeforeCursor;

    document.body.appendChild(tempElement);
    const textHeight = tempElement.offsetHeight;
    const textWidth = tempElement.offsetWidth;
    document.body.removeChild(tempElement);

    const textareaRect = textarea.getBoundingClientRect();
    const lineHeight = parseInt(getComputedStyle(textarea).lineHeight);

    return {
      top: textareaRect.top + textHeight + lineHeight,
      left: textareaRect.left + (textWidth % textarea.clientWidth),
    };
  }

  if (!isVisible || filteredUsers.length === 0) return null;

  const position = getAutocompletePosition();

  return (
    <div
      ref={autocompleteRef}
      className="fixed z-50 bg-canvas border border-edge rounded-lg shadow-lg max-w-xs"
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      <div className="p-2 text-xs text-ink-muted border-b border-gray-100">
        Press ↑↓ to navigate, Enter to select
      </div>
      <div className="max-h-48 overflow-y-auto">
        {filteredUsers.map((user, index) => (
          <div
            key={user.id}
            className={`p-3 cursor-pointer border-b border-gray-50 last:border-b-0 ${
              index === selectedIndex ? 'bg-primary-50' : 'hover:bg-gray-50'
            }`}
            onClick={() => handleSelectUser(user)}
          >
            <div className="flex items-center gap-2">
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt=""
                  className="w-6 h-6 rounded-full"
                />
              ) : (
                <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center">
                  <span className="text-xs font-medium text-ink-muted">
                    {user.displayName?.[0] || user.username[0].toUpperCase()}
                  </span>
                </div>
              )}
              <div className="min-w-0">
                <div className="text-sm font-medium text-ink truncate">
                  {user.displayName || user.username}
                </div>
                <div className="text-xs text-ink-muted">@{user.username}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}